import React, { useMemo } from 'react'
import axios from 'axios'
import { HttpStatus } from '@notino/react-toolkit/renderer/fragment/contexts/HttpContext'

// hooks
import usePageData from '../hooks/usePageData'

// utils
import { ERROR_PAGE_CODE } from './enums'

// types
import { BasePageData, BasePageQuery, BasePageRouteProps, ExtendedContextProps, PageContainerQuery, PageContainer, PageProps } from '../types/types'

type CreatePageContainerReturnType<TData extends BasePageData, TQuery extends BasePageQuery, TRouteProps = BasePageRouteProps> = {
	identifier: string
	PageComponent: React.ComponentType<PageProps<TData, TQuery, TRouteProps>>
	/**
	 * Function to fetch data on the server.
	 * Should return only the page data (not wrapped in { success: true }).
	 */
	initDefaultData: (args: ExtendedContextProps<PageContainerQuery<TQuery> & TRouteProps>) => Promise<TData>
}

/**
 * Factory function for creating strongly-typed page containers with consistent error handling and query sanitization.
 *
 * This function wraps your page component and its server-side data-fetching logic, ensuring:
 * - All page containers have a unified structure and error handling.
 * - The query prop passed to the page component omits user-location keys (latMy, lonMy) to prevent accidental usage.
 * - Errors thrown in initDefaultData are caught and mapped to appropriate error pages (401, 403, 404, etc.).
 * - The identifier and initDefaultData are attached to the container for framework integration.
 *
 * Usage:
 *   export default createPageContainer({
 *     identifier: 'SomePage',
 *     PageComponent: SomePage,
 *     initDefaultData: async (args) => { ... }
 *   })
 *
 * @template TData      The shape of the page data.
 * @template TQuery     The shape of the query parameters.
 * @template TRouteProps The shape of additional route props.
 */

const createPageContainer = <TData extends BasePageData, TQuery extends BasePageQuery, TRouteProps = BasePageRouteProps>({
	identifier,
	PageComponent,
	initDefaultData
}: CreatePageContainerReturnType<TData, TQuery, TRouteProps>) => {
	const Container: PageContainer<TData, TQuery, TRouteProps> = (props) => {
		const { query, ...restProps } = props
		const { pageData, ...restUsePageDataProps } = usePageData<TData, TQuery, TRouteProps>(Container, props)

		const pageProps = useMemo(() => {
			/**
			 * Always use values from `MyLocationContext` when working with the user's location, rather than relying on the query parameters.
			 * Although the query includes `latMy` and `lonMy`, these are intended solely for the server to fetch the correct data when the user's detected location is passed via URL query parameters.
			 * To prevent accidental usage of these parameters, this sanitized `query` object excludes user-related location keys.
			 */
			const resolvedQuery = { ...query }
			delete resolvedQuery.latMy
			delete resolvedQuery.lonMy

			return {
				...pageData,
				...restUsePageDataProps,
				...restProps,
				query: resolvedQuery
			} as PageProps<TData, TQuery, TRouteProps>
		}, [pageData, restUsePageDataProps, restProps, query])

		if (!pageData) {
			return null
		}

		if (!pageData.success) {
			return (
				<HttpStatus status={pageData.statusCodeToRender || ERROR_PAGE_CODE.GENERAL_ERROR} {...props}>
					{null}
				</HttpStatus>
			)
		}

		return <PageComponent {...pageProps} />
	}

	Container.initDefaultData = async (args) => {
		try {
			const data = await initDefaultData(args)
			return { success: true, ...data }
		} catch (error) {
			let statusCodeToRender: ERROR_PAGE_CODE = ERROR_PAGE_CODE.GENERAL_ERROR

			if (axios.isAxiosError(error)) {
				switch (error.response?.status) {
					/**
					 * The Notino layout service currently handles 401 and 403 errors as 404 pages.
					 * However, this logic is prepared to accommodate potential future changes.
					 */
					case 404:
						statusCodeToRender = ERROR_PAGE_CODE.NOT_FOUND
						break
					case 401:
						statusCodeToRender = ERROR_PAGE_CODE.UNAUTHORIZED
						break
					case 403:
						statusCodeToRender = ERROR_PAGE_CODE.FORBIDDEN
						break
					default:
						break
				}
			}
			return { success: false, statusCodeToRender }
		}
	}

	Container.identifier = identifier

	return Container
}

export default createPageContainer
